// components/ExpressCheckoutButton.tsx
import React, { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { getCartCode } from '../../../../utils/cartStorage';
import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';

interface ExpressCheckoutButtonProps {
  amount: number;
  currency?: string;
  publishableKey: string;
  /** Callback quando descobre se há wallets disponíveis (Apple/Google Pay) */
  onWalletsAvailable?: (available: boolean) => void;
}

const ExpressCheckoutButton: React.FC<ExpressCheckoutButtonProps> = ({
  amount,
  currency = 'brl',
  publishableKey,
  onWalletsAvailable,
}) => {
  const amountCents = useMemo(() => Math.round(Number(amount || 0) * 100), [amount]);
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);

  if (!publishableKey) return null;
  if (!amountCents || amountCents <= 0) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'payment',
        amount: amountCents,
        currency: currency.toLowerCase(),
        // ✅ Remove Link: Link só aparece quando 'link' está habilitado junto com 'card'
        paymentMethodTypes: ['card'],
      }}
    >
      <ExpressCheckoutWrapper currency={currency} onWalletsAvailable={onWalletsAvailable} />
    </Elements>
  );
};

const ExpressCheckoutWrapper: React.FC<{ currency: string; onWalletsAvailable?: (available: boolean) => void }> = ({
  currency,
  onWalletsAvailable,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [eceHasWallets, setEceHasWallets] = useState(false);

  const reportErrorAndClose = (message: string, event: any) => {
    setConfirmError(message);
    if (typeof event?.paymentFailed === 'function') {
      event.paymentFailed({ error: { message } });
    }
  };

  const handleConfirm = async (event: any) => {
    try {
      setConfirmError(null);

      if (!stripe || !elements) {
        reportErrorAndClose('Stripe ainda não carregou. Tente novamente.', event);
        return;
      }

      setIsConfirming(true);

      const expressType = String(event?.expressPaymentType || '').toLowerCase();
      const paymentMethod = expressType === 'apple_pay' ? 'apple_pay' : expressType === 'google_pay' ? 'google_pay' : '';
      if (!paymentMethod) {
        reportErrorAndClose('Método de wallet inválido.', event);
        return;
      }

      const cartCode = getCartCode();
      if (!cartCode) {
        reportErrorAndClose('Carrinho não encontrado. Recarregue a página e tente novamente.', event);
        return;
      }

      // Submete dados coletados pelo Express Checkout Element
      if (typeof (elements as any).submit === 'function') {
        const { error: submitError } = await (elements as any).submit();
        if (submitError) {
          reportErrorAndClose(submitError.message || 'Erro ao validar dados do pagamento.', event);
          return;
        }
      }

      // ✅ Deferred intent: cria o PaymentIntent só no confirm
      const createIntentRes = await fetch(`${import.meta.env.VITE_BACKEND_URL}/stripe/wallets/create-intent-from-cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartCode,
          paymentMethod,
          currency,
        }),
      });
      const createIntentData = await createIntentRes.json().catch(() => null);
      if (!createIntentRes.ok) {
        const msg = createIntentData?.message || createIntentData?.error?.message || 'Erro ao iniciar pagamento.';
        reportErrorAndClose(msg, event);
        return;
      }

      const clientSecret = createIntentData?.clientSecret;
      if (!clientSecret) {
        reportErrorAndClose('Erro ao iniciar pagamento (clientSecret ausente).', event);
        return;
      }

      const { error } = await (stripe as any).confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        reportErrorAndClose(error.message || 'Erro ao confirmar pagamento.', event);
        return;
      }

      const transactionId = String(createIntentData?.transactionId || '').trim();
      if (!transactionId) {
        reportErrorAndClose('Pagamento confirmado, mas transactionId ausente.', event);
        return;
      }

      // ✅ Criar Order somente após pagamento confirmado
      const createOrderRes = await fetch(`${import.meta.env.VITE_BACKEND_URL}/stripe/wallets/create-order-after-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartCode,
          transactionId,
          paymentMethod,
        }),
      });
      const createOrderData = await createOrderRes.json().catch(() => null);
      if (!createOrderRes.ok) {
        const msg = createOrderData?.message || createOrderData?.error?.message || 'Pagamento confirmado, mas falhou ao criar pedido.';
        reportErrorAndClose(msg, event);
        return;
      }

      const orderId = String(createOrderData?.orderId || '').trim();
      if (!orderId) {
        reportErrorAndClose('Pedido criado, mas orderId ausente.', event);
        return;
      }

      // Buscar dados do carrinho para preencher view de sucesso
      const cartRes = await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${encodeURIComponent(cartCode)}`);
      const cartData = await cartRes.json().catch(() => null);

      const fullName = String(cartData?.customerData?.name || '').trim();
      const [firstName, ...rest] = fullName.split(' ').filter(Boolean);
      const lastName = rest.join(' ') || '-';

      const checkoutResponse: any = {
        order: {
          id: Number(orderId),
          status: 'completed',
          totalPrice: Number(cartData?.totalPrice || 0),
          totalDiscounts: Number(cartData?.totalDiscounts || 0),
          gatewayInfos: {
            gatewayUsed: 'stripe',
            gatewayId: transactionId,
          },
          postcode: cartData?.customerData?.address?.postcode,
          addressStreet: cartData?.customerData?.address?.street,
          addressStreetNumber: cartData?.customerData?.address?.streetNumber,
          addressStreetComplement: cartData?.customerData?.address?.streetComplement,
          addressStreetDistrict: cartData?.customerData?.address?.streetDistrict,
          addressCity: cartData?.customerData?.address?.city,
          addressState: cartData?.customerData?.address?.state,
          items: [],
          customerId: 0,
          storeId: cartData?.storeId || 0,
        },
        customer: {
          id: 0,
          firstName: firstName || 'Cliente',
          lastName,
          email: cartData?.customerData?.email || '',
          phone: cartData?.customerData?.phone || '',
          documentNumber: cartData?.customerData?.document || '',
          storeId: cartData?.storeId || 0,
        },
        offer: {
          nanoId: cartData?.offerNanoId || '',
          uuid: cartData?.offerNanoId || '',
          cart: { metadata: { line_items: cartData?.items || [] } },
        },
        paymentMethod: {
          paymentMethod,
        },
        paymentData: {},
        paymentStatus: 'success',
      };

      try {
        // @ts-ignore
        if (typeof window !== 'undefined' && (window as any).setCheckoutOrderResponse) {
          // @ts-ignore
          (window as any).setCheckoutOrderResponse(checkoutResponse);
        }
      } catch {}

      // Marcar carrinho como concluído (não bloqueia)
      try {
        await fetch(`${import.meta.env.VITE_BACKEND_URL}/carts/${encodeURIComponent(cartCode)}/concluded`, {
          method: 'PATCH',
        });
      } catch {}
    } catch (err: any) {
      const msg = err?.message || 'Erro ao confirmar pagamento.';
      setConfirmError(msg);
      if (typeof event?.paymentFailed === 'function') {
        event.paymentFailed({ error: { message: msg } });
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const options = {
    buttonType: {
      type: 'buy',
      shape: 'rectangular',
    },
    layout: { maxColumns: 1, maxRows: 2 },
    // Tenta priorizar Apple/Google quando disponíveis
    paymentMethodOrder: ['apple_pay', 'google_pay'],
    // Desativa Link para não exibir junto com Apple/Google Pay
    paymentMethods: { link: 'never' as const },
  };

  const handleReady = ({ availablePaymentMethods }: any) => {
    const hasApple = !!availablePaymentMethods?.applePay;
    const hasGoogle = !!availablePaymentMethods?.googlePay;
    const has = hasApple || hasGoogle;
    setEceHasWallets(has);
    onWalletsAvailable?.(has);
  };

  // Esconde completamente se só tiver Link (ou nenhuma wallet)
  if (!eceHasWallets) {
    return (
      <div className="hidden">
        <ExpressCheckoutElement
          options={options}
          onReady={handleReady}
          onConfirm={handleConfirm}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mt-2">
        <ExpressCheckoutElement
          options={options}
          onReady={handleReady}
          onConfirm={handleConfirm}
        />
      </div>
      {confirmError && <p className="text-xs text-red-500 mt-2">{confirmError}</p>}
      {isConfirming && <p className="text-xs text-gray-500 mt-2">Confirmando pagamento...</p>}
    </div>
  );
};


export default ExpressCheckoutButton;
